import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { ErrorState } from '@/design/states';

/**
 * Last-resort boundary (ARCH-003).
 *
 * A render crash inside one screen must not leave a clinician staring at a
 * white page mid-consultation. The recovery action is a full reload rather than
 * a state reset, because a component that threw during render has already lost
 * whatever invariant it depended on.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Deliberately no patient data: the props of the failing subtree are not
    // logged, only the stack. PRD 21.5 forbids clinical payload in telemetry.
    console.error('Unhandled render error', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
          <ErrorState
            title="حدث خطأ غير متوقع"
            body="تعذّر عرض هذه الشاشة. لم تُفقد أي بيانات محفوظة."
            onRetry={() => window.location.reload()}
          />
        </div>
      );
    }
    return this.props.children;
  }
}
